import { useRef, useState } from 'react'
import {
  Box,
  Flex,
  HStack,
  IconButton,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react'
import { motion, useReducedMotion } from 'framer-motion'
import {
  RefreshCw,
  Search,
  SearchX,
  Sparkles,
  TriangleAlert,
} from 'lucide-react'
import { createFileRoute } from '@tanstack/react-router'
import { MAX_CONTAINER_WIDTH } from '@/constants/dimensions'
import { SEARCH_DEBOUNCE_MS, SEARCH_MIN_LENGTH } from '@/constants/contentFeed'
import { isSearchable } from '@/utils/contentSearchQuery'
import ContentCardSkeletonGrid from '@/modules/content/common/ContentCardSkeletonGrid'
import ContentEmptyState from '@/modules/content/common/ContentEmptyState'
import ContentGrid from '@/modules/content/common/ContentGrid'
import ContentFilterModal from '@/modules/content/feed/filters/ContentFilterModal'
import FeedSearchInput from '@/modules/content/feed/FeedSearchInput'
import FilterButton from '@/modules/content/feed/filters/parts/FilterButton'
import {
  countActiveFilters,
  EMPTY_CONTENT_FILTERS,
  TContentFilters,
} from '@/modules/content/feed/filters/model/types'
import useFeedContent from '@/hooks/content/useFeedContent'
import useDebouncedValue from '@/hooks/useDebouncedValue'
import useInfiniteScrollSentinel from '@/hooks/useInfiniteScrollSentinel'
import { getPersonalizedFeedInfiniteOptions } from '@/utils/queryOptions'
import { text } from '@/theme/colors/palettes'

// A gentle fade for the non-grid states (hint / empty / error), so they don't hard-cut in.
const FadeIn = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.2 }}
    style={{ width: '100%' }}
  >
    {children}
  </motion.div>
)

const CreatorFeed = () => {
  // The applied filter set drives the grid; the modal edits a draft and commits it here.
  const [filters, setFilters] = useState<TContentFilters>(EMPTY_CONTENT_FILTERS)
  const [isFilterOpen, setFilterOpen] = useState(false)

  // Search input in local state (never the URL) — debounced before it drives the query.
  const [searchInput, setSearchInput] = useState('')
  const query = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS)

  const {
    content,
    isRanked,
    isSearching,
    isLoading,
    isError,
    isTransitioning,
    refetch,
    refresh,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useFeedContent({ filters, query })

  const activeCount = countActiveFilters(filters)

  // The user has typed something, but it hasn't reached a searchable length yet. Gauged
  // from the raw input (not the debounced query) so the hint appears immediately.
  const searchTooShort =
    searchInput.trim().length > 0 && !isSearchable(searchInput)

  // Retain the last non-empty grid across source switches. keepPreviousData only spans a
  // single query key, so ranked→filtered→search would otherwise flash a skeleton each
  // time. Holding the previous cards (dimmed) while the new source loads keeps it smooth;
  // a skeleton then appears only on the very first load, when there is nothing to hold.
  const lastContentRef = useRef(content)
  if (content.length > 0) lastContentRef.current = content

  const showingNewSource =
    content.length === 0 && isLoading && lastContentRef.current.length > 0
  const gridItems = content.length > 0 ? content : lastContentRef.current
  const showGrid = content.length > 0 || showingNewSource
  const dimGrid = isTransitioning || showingNewSource

  const loadMoreRef = useInfiniteScrollSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  // Each refresh spins the icon one turn — a small confirmation that "a new draw" happened,
  // which is otherwise invisible until the cards re-stagger in.
  const reduceMotion = useReducedMotion()
  const [refreshTurns, setRefreshTurns] = useState(0)
  const onRefresh = () => {
    refresh()
    if (!reduceMotion) setRefreshTurns((t) => t + 1)
  }

  return (
    <VStack width={MAX_CONTAINER_WIDTH} gap={'20px'} alignItems={'stretch'}>
      <HStack
        width={'100%'}
        justifyContent={'space-between'}
        alignItems={'center'}
        flexWrap={'wrap'}
        gap={'12px'}
      >
        <Text variant={'loraMediumTitle'} color={text.darkBlue}>
          Explore
        </Text>
        <HStack gap={'12px'} flexWrap={'wrap'}>
          <FeedSearchInput value={searchInput} onChange={setSearchInput} />
          <FilterButton
            activeCount={activeCount}
            onClick={() => setFilterOpen(true)}
          />
          {/* Refresh mints a new draw of the ranked feed, and is meaningful only there —
              searching and filtering are deterministic. Kept mounted but hidden when not
              ranked so its slot never collapses, otherwise the search input and Filters
              button would shift sideways every time you start/stop searching. */}
          <Tooltip
            label={'Show me something different'}
            placement={'top'}
            hasArrow
            isDisabled={!isRanked}
          >
            <IconButton
              aria-label={'Refresh feed'}
              icon={
                <motion.span
                  style={{ display: 'inline-flex' }}
                  animate={{ rotate: refreshTurns * 360 }}
                  transition={{ duration: 0.6, ease: 'easeInOut' }}
                >
                  <RefreshCw size={18} />
                </motion.span>
              }
              variant={'ghost'}
              color={text.mediumBlue}
              borderRadius={'full'}
              onClick={onRefresh}
              visibility={isRanked ? 'visible' : 'hidden'}
              pointerEvents={isRanked ? 'auto' : 'none'}
              tabIndex={isRanked ? 0 : -1}
              aria-hidden={!isRanked}
            />
          </Tooltip>
        </HStack>
      </HStack>

      {/* One stable container. The grid stays mounted across data changes and only dims
          during a transition (no unmount/remount, no full-block crossfade), so switching
          source or filters no longer flashes. Skeleton / empty / error fade in gently
          only when there is genuinely no grid to show. */}
      <Box position={'relative'} width={'100%'}>
        {searchTooShort ? (
          // The user is typing a search that hasn't reached the backend minimum yet. Say
          // so, rather than silently leaving the ranked/filtered feed on screen (which
          // reads as "search returned everything").
          <FadeIn key={'hint'}>
            <ContentEmptyState
              icon={Search}
              title="Keep typing"
              description={`Type at least ${SEARCH_MIN_LENGTH} characters to search.`}
            />
          </FadeIn>
        ) : showGrid ? (
          <>
            <ContentGrid content={gridItems} isDimmed={dimGrid} />

            {/* Spinner over the dimmed grid while the next combination loads. */}
            {dimGrid && (
              <Flex
                position={'absolute'}
                inset={0}
                justifyContent={'center'}
                alignItems={'flex-start'}
                paddingTop={'120px'}
                pointerEvents={'none'}
              >
                <Spinner color={'primary.500'} size={'lg'} />
              </Flex>
            )}

            {/* Infinite-scroll sentinel + next-page indicator (only while more pages exist). */}
            {content.length > 0 && hasNextPage && (
              <Box ref={loadMoreRef} width={'100%'}>
                {isFetchingNextPage && (
                  <HStack
                    width={'100%'}
                    justifyContent={'center'}
                    paddingY={'24px'}
                  >
                    <Spinner color={'primary.500'} size={'md'} />
                  </HStack>
                )}
              </Box>
            )}
          </>
        ) : isLoading ? (
          <ContentCardSkeletonGrid count={6} />
        ) : isError ? (
          <FadeIn key={'error'}>
            <ContentEmptyState
              icon={TriangleAlert}
              title="Couldn’t load the feed"
              description="Something went wrong while loading content. Please try again."
              actionLabel="Try again"
              onAction={() => refetch()}
            />
          </FadeIn>
        ) : isSearching ? (
          <FadeIn key={'no-results'}>
            <ContentEmptyState
              icon={SearchX}
              title="No results found"
              description="Nothing matched that search. Try a different word, or adjust your filters."
            />
          </FadeIn>
        ) : activeCount > 0 ? (
          <FadeIn key={'no-matches'}>
            <ContentEmptyState
              icon={SearchX}
              title="No matches found"
              description="Nothing matches the filters you’ve selected. Try removing a few, or clear them all to explore everything."
              actionLabel="Clear filters"
              onAction={() => setFilters(EMPTY_CONTENT_FILTERS)}
            />
          </FadeIn>
        ) : (
          <FadeIn key={'empty'}>
            <ContentEmptyState
              icon={Sparkles}
              title="Nothing here yet"
              description="New content will appear here as creators publish it. Check back soon."
            />
          </FadeIn>
        )}
      </Box>

      <ContentFilterModal
        isOpen={isFilterOpen}
        onClose={() => setFilterOpen(false)}
        applied={filters}
        onApply={setFilters}
      />
    </VStack>
  )
}

export const Route = createFileRoute('/_authenticated/_explore/explore/')({
  staticData: { title: 'Explore' },
  loader: ({ context }) =>
    context.queryClient.ensureInfiniteQueryData(
      getPersonalizedFeedInfiniteOptions(),
    ),
  component: CreatorFeed,
})
